import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";

import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import Fab from "@mui/material/Fab";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import { ButtonUI, KMKDrawer } from "../../../../../Common";
import { showToast } from "../../../../../../store/actions";
import { checkReservedKeywords } from "../../../../../../util/restrictedStatement";
import Details from "./Detail";
import MDM from "./DataMaster";

const drawerWidth = 420;

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  closeIcon: {
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "25px 30px",
    "& .errorMessage .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "flex-start",
      border: "none",
    },
    "& .errorMessage .MuiAlert-message": {
      color: "#C72C2C",
    },
    "& .errorMessage": {
      marginBottom: 0,
    },
  },
  fabButtonActive: {
    background: "linear-gradient(95deg, #11ab97 0%, #27354f 100%);",
    color: "white",
    boxShadow: "none",
    minWidth: "180px",
    "&:hover": {
      color: "white",
    },
    textTransform: "none",
  },
  fabButton: {
    background: "white",
    color: "black",
    boxShadow: "none",
    minWidth: "180px",
    "&:hover": {
      color: "black",
    },
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

let errorMessage = "";

const AddField = ({ setOpenAddField, addField, fields, currentField, openTab, FieldSettings, newEntity }) => {
  const [currentFieldObj, setCurrentFieldObj] = useState({});
  const [details, setDetails] = useState(false);
  const [mdm, setMdm] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // State Data for Detail Tab
  const [attribute, setAttribute] = useState("");
  const [attributeError, setAttributeError] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("NoOption");
  const [length, setLength] = useState("NoOption");
  const [oldLength, setOldLength] = useState("NoOption");
  const [openLengthList, setOpenLengthList] = useState(false);
  const detailFormData = {
    loading,
    setLoading,
    attribute,
    setAttribute,
    attributeError,
    setAttributeError,
    description,
    setDescription,
    type,
    setType,
    length,
    setLength,
    oldLength,
    setOldLength,
    openLengthList,
    setOpenLengthList,
  };
  const enableDetailsSave = () => {
    return (
      !(detailFormData.attribute && detailFormData.type && detailFormData.type !== "NoOption") ||
      (detailFormData.openLengthList && detailFormData.length === "NoOption")
    );
  };

  // State Data for MDM Tab
  const [dataSourceList, setDataSourceList] = useState([]);
  const [survivorShipPriority, setSurvivorShipPriority] = useState("Source-based");

  // State for Deduplication
  const [useForDeduplication, setUseForDeduplication] = useState(false);
  const [nullPriority, setNullPriority] = useState(false);
  const [valueType, setValueType] = useState("Select Value Type");
  const [missingValue, setMissingValue] = useState("Select Missing Value");

  const mdmFormData = {
    survivorShipPriority,
    setSurvivorShipPriority,
    dataSourceList,
    setDataSourceList,
    useForDeduplication,
    setUseForDeduplication,
    nullPriority,
    setNullPriority,
    valueType,
    setValueType,
    missingValue,
    setMissingValue,
  };

  const enableMDMSave = () => {
    return (
      (mdmFormData.useForDeduplication && mdmFormData.valueType === "Select Value Type") ||
      (mdmFormData.useForDeduplication && mdmFormData.missingValue === "Select Missing Value") ||
      !mdmFormData.survivorShipPriority
    );
  };

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const fieldsRef = useRef(fields);
  const currentFieldRef = useRef(currentField);
  const onClose = () => setOpenAddField(false);

  const validateAttributeName = () => {
    if (checkReservedKeywords(detailFormData.attribute)) return "Reserved keyword.";
    else if (
      fields.some(
        (field) =>
          field.name.toLowerCase() === detailFormData.attribute.toLowerCase() &&
          currentField.toLowerCase() !== detailFormData.attribute.toLowerCase()
      )
    )
      return "Name must be Unique.";
    else if (detailFormData.attribute.length < 2 || detailFormData.attribute.length > 50)
      return "Name must be between 2 to 50 characters.";
    return "";
  };

  const handleSaveDetails = (saveBy) => {
    setLoading(true);
    if (details) {
      errorMessage = validateAttributeName();
      if (errorMessage) setAttributeError(errorMessage);
      else {
        addField({
          name: detailFormData.attribute,
          type: detailFormData.type !== "NoOption" ? detailFormData.type : "",
          description,
          length: detailFormData.length !== "NoOption" ? detailFormData.length : "",
        });

        //Update length into the OldLength var to disbale length less then previouly selected
        setOldLength(length);

        FieldSettings.setCurrentEntityObj(FieldSettings.currentEntityObj);

        //Update the current Field when rename the attribute
        FieldSettings.setCurrentField(detailFormData.attribute);
      }
    }

    if (mdm) {
      let result = {};
      let submitData = {};

      const dsNames = dataSourceList.map((ds) => ds.Data_Source);
      // If more than one mapping is done than adding MDM property in that field
      if (dsNames.length > 1) {
        submitData = {
          mdm: {
            nullPriority,
            survivorship: mdmFormData.survivorShipPriority,
            priority: dsNames,
          },
        };
      }

      result = FieldSettings.dataTableRef.current.getData().map((item) => {
        if (!item?.mdm) item.mdm = {};
        if (!item?.dedupe) item.dedupe = {};

        if (item.name.toLowerCase() === FieldSettings.currentField.toLowerCase()) {
          item.mdm = submitData?.mdm || {};

          if (mdmFormData.useForDeduplication) {
            item.dedupe = {
              valueType: mdmFormData.valueType,
              missingValue: mdmFormData.missingValue,
            };
          } else {
            item.dedupe = {};
          }
        }

        FieldSettings.dataTableRef.current.updateRow(item.name, item);
        return item;
      });

      FieldSettings.setCurrentEntityObj(FieldSettings.currentEntityObj);

      // Set Fields of "Attribute" table
      FieldSettings.setFields(result);
    }

    if (errorMessage === "") {
      if (saveBy !== "auto") {
        dispatch(
          showToast({
            message: `Attribute ${currentField ? "saved" : "added"} successfully.`,
            type: "success",
          })
        );
        setOpenAddField(false);
      }
    }

    setLoading(false);
  };

  const handleTabclick = (clickedTab) => {
    setFormErrorMessage("");

    if (details && enableDetailsSave()) setFormErrorMessage("Please enter required(*) fields.");
    else if (mdm && enableMDMSave()) setFormErrorMessage("Please enter required(*) fields.");
    else {
      handleSaveDetails("auto");
      if (errorMessage === "") {
        if (clickedTab === "details") handleDetails();
        else if (clickedTab === "MDM") handleMDM();
      }
    }
  };

  const handleDetails = () => {
    setDetails(true);
    setMdm(false);
  };

  const handleMDM = () => {
    setDetails(false);
    setMdm(true);
  };

  useEffect(() => {
    if (currentFieldRef.current) {
      const tField = fieldsRef.current.find((field) => field.name === currentFieldRef.current);
      setCurrentFieldObj(tField);
    }
    openTab === "details" ? handleDetails() : handleMDM();
  }, [openTab]);

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar />
      <Box className={classes.container} sx={{ marginBottom: "0" }} role="presentation">
        <Grid container spacing={2}>
          <Grid size={10}>
            <Typography className={classes.heading} variant="h6" gutterBottom component="div">
              Attribute : <span>{currentField || "New Field"}</span>
            </Typography>
          </Grid>
          <Grid size={2}>
            <Button className={classes.closeIcon} onClick={onClose}>
              <CloseIcon />
            </Button>
          </Grid>
        </Grid>

        <Box
          sx={{
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ButtonGroup className="tabtext" sx={{ borderRadius: "200px", width: "360px" }} variant="contained">
            <Fab
              variant="extended"
              size="small"
              className={details ? classes.fabButtonActive : classes.fabButton}
              onClick={() => handleTabclick("details")}
            >
              Details
            </Fab>
            <Fab
              variant="extended"
              size="small"
              className={mdm ? classes.fabButtonActive : classes.fabButton}
              onClick={() => handleTabclick("MDM")}
              disabled={!currentFieldObj?.mappings?.length}
            >
              MDM
            </Fab>
          </ButtonGroup>
        </Box>
        {formErrorMessage && ((details && enableDetailsSave()) || (mdm && enableMDMSave())) ? (
          <Stack className="errorMessage" sx={{ width: "100%", marginBottom: "20px" }} spacing={2}>
            <Alert severity="error">{formErrorMessage}</Alert>
          </Stack>
        ) : (
          ""
        )}
        {details && (
          <Details
            fields={fields}
            currentField={currentField}
            detailFormData={detailFormData}
            newEntity={newEntity}
            validateAttributeName={validateAttributeName}
          />
        )}
        {mdm && <MDM Settings={FieldSettings} mdmFormData={mdmFormData} />}
        <Box my={2}>
          <ButtonUI
            disabled={(details && enableDetailsSave()) || (mdm && enableMDMSave()) || loading}
            variant="contained"
            color="success"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSaveDetails("manual")}
          >
            Save
          </ButtonUI>
          <ButtonUI
            variant="contained"
            color="secondary"
            style={{ marginLeft: 15 }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClose}
          >
            Cancel
          </ButtonUI>
        </Box>
      </Box>
    </KMKDrawer>
  );
};

export default AddField;
